import { FC, useMemo, useState } from 'react';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, useModal } from 'core';
import { RectangularButtonFilled, RectangularButtonHollow } from 'shared/components/buttons';
import { AppTester } from 'supporting';
import { API, Endpoints, Project } from '@brg/api';

const OuterWrapper = styled(FlexBox)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background: rgba(21, 21, 20, 0.60);
  backdrop-filter: blur(10px);
  z-index: 9999999999;
`;

const Wrapper = styled(FlexBox)`
  background-color: ${({ theme }) => theme.ageModal.bg};
  display: flex;
  width: 564px;
  padding: 32px 40px 40px 40px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;

  @media (max-width: ${LayoutConst.screen.sm}px) {
    width: 100%;
    border-radius: 8px 8px 0 0;
    position: fixed;
    bottom: 0;
  }
`;

const Title = styled(FInter.SectionTitle)<{ isMobile: boolean }>`
  letter-spacing: -0.6px;
  margin-bottom: 8px;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 23px;
    font-style: normal;
    font-weight: 600;
    line-height: 29px;
    letter-spacing: -0.46px;
  }

  color: ${({ theme }) => theme.not_saved_modal.title};
`;

const Description = styled(FInter.SectionTitle)<{ isMobile: boolean, align?: string }>`
  margin-bottom: 32px;
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.3px;
    margin-bottom: 24px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }
  
  color: ${({ theme }) => theme.not_saved_modal.title};
  text-align: ${({ align }) => align || 'initial'};
`;

const Button = css`
  width: calc((100% - 16px) / 2);
  height: 46px !important;
  font-weight: bold;

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    margin: 0;
  }
`;

const ButtonLeft = styled(RectangularButtonHollow)<{ isMobile: boolean }>`
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 77px;
  }

  ${Button};
`;

const ButtonRight = styled(RectangularButtonFilled)<{ isMobile: boolean }>`
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 77px;
  }
  
  ${Button};
`;

const ButtonWrapper = styled(FlexBox)`
  flex-direction: row;
  gap: 16px;
  
  @media (max-width: ${LayoutConst.screen.sm}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FullWidthButton = styled(RectangularButtonFilled)<{ isMobile: boolean }>`
  width: 100%;
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;
`;

interface RequestPublicationModalProps {
  project: Project;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RequestPublicationModal: FC<RequestPublicationModalProps> = ({ project, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isRequestSended = useMemo(() => project?.contexts?.includes('unbanrequest'), [project]);

  const hasApprovedContexts = useMemo(() => {
    return project?.contexts?.some(
      (ctx: string) => !['banned', 'places', 'hsedesigntemp', 'unbanrequest'].includes(ctx)
    );
  }, [project]);
  
  const shouldShowAlreadyUnderReview = isRequestSended || hasApprovedContexts;
  
  const [requestSent, setRequestSent] = useState(false);
  
  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onCancel();
  };

  const handleConfirm = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsSubmitted(true);
  };

  const handleClose = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (hasApprovedContexts) {
      onCancel();
      return;
    }
    
    if (requestSent) {
      onCancel();
      return;
    }
    
    try {
      const response = await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body({
          projectId: project?.projectId,
          contexts: [
            {
              context: "unbanrequest",
              operation: 1,
            }
          ],
        })
        .send();
      
      if (!response.ok) {
        if (response.status === 400) {
          setRequestSent(true);
          onCancel();
          return;
        }
        
        throw new Error(`API error: ${response.status}`);
      }
      
      setRequestSent(true);
      
      window.location.reload();
    } catch (error: any) {
      setRequestSent(true);
      onCancel();
    }
  };

  return (
    <OuterWrapper onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}>
      <Wrapper direction={'column'} width={'100%'} justify='flex-start' align='flex-start' onClick={(e) => e.stopPropagation()}>
        <FlexBox direction={'column'} directionMobile='column' width={'100%'} justify={'flex-start'}
                 justifyMobile='center'
                 align={'flex-start'} alignMobile={'flex-start'}>
          {shouldShowAlreadyUnderReview ? (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.unbanrequest_title')}
              </Title>
              <FlexBox offsetTop={32} width="100%">
                <FullWidthButton
                  isMobile={isMobile}
                  onClick={handleCancel}
                >
                  {t('request_publication_modal.success_button')}
                </FullWidthButton>
              </FlexBox>
            </>
          ) : isSubmitted ? (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.success_title')}
              </Title>
              <FlexBox offsetTop={32} width="100%">
                <FullWidthButton
                  isMobile={isMobile}
                  onClick={handleClose}
                >
                  {t('request_publication_modal.success_button')}
                </FullWidthButton>
              </FlexBox>
            </>
          ) : (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.title')}
              </Title>
              <Description isMobile={isMobile}>
                {t('request_publication_modal.description')}
              </Description>
              <ButtonWrapper offsetTop={8} width="100%">
                <ButtonLeft
                  isMobile={isMobile}
                  onClick={handleCancel}
                >
                  {t('request_publication_modal.cancel')}
                </ButtonLeft>
                <ButtonRight
                  isMobile={isMobile}
                  onClick={handleConfirm}
                >
                  {t('request_publication_modal.confirm')}
                </ButtonRight>
              </ButtonWrapper>
            </>
          )}
        </FlexBox>
      </Wrapper>
    </OuterWrapper>
  );
};

import { FC, useMemo, useState } from 'react';
import { Colors, FInter, FlexBox, useIsMobile } from '@brg/ui';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, useModal } from 'core';
import { RectangularButtonFilled, RectangularButtonHollow } from 'shared/components/buttons';
import { AppTester } from 'supporting';
import { API, Endpoints, Project } from '@brg/api';

const OuterWrapper = styled(FlexBox)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  background: rgba(21, 21, 20, 0.60);
  backdrop-filter: blur(10px);
  z-index: 9999999999;
`;

const Wrapper = styled(FlexBox)`
  background-color: ${({ theme }) => theme.ageModal.bg};
  display: flex;
  width: 564px;
  padding: 32px 40px 40px 40px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;

  @media (max-width: ${LayoutConst.screen.sm}px) {
    width: 100%;
    border-radius: 8px 8px 0 0;
    position: fixed;
    bottom: 0;
  }
`;

const Title = styled(FInter.SectionTitle)<{ isMobile: boolean }>`
  letter-spacing: -0.6px;
  margin-bottom: 8px;
  font-size: 30px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 23px;
    font-style: normal;
    font-weight: 600;
    line-height: 29px;
    letter-spacing: -0.46px;
  }

  color: ${({ theme }) => theme.not_saved_modal.title};
`;

const Description = styled(FInter.SectionTitle)<{ isMobile: boolean, align?: string }>`
  margin-bottom: 32px;
  font-size: 17px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: -0.34px;

  @media (max-width: ${LayoutConst.screen.mac13}px) {
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.3px;
    margin-bottom: 24px;
  }

  @media (max-width: ${LayoutConst.screen.s}px) {
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }
  
  color: ${({ theme }) => theme.not_saved_modal.title};
  text-align: ${({ align }) => align || 'initial'};
`;

const Button = css`
  width: calc((100% - 16px) / 2);
  height: 46px !important;
  font-weight: bold;

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: 100%;
    margin: 0;
  }
`;

const ButtonLeft = styled(RectangularButtonHollow)<{ isMobile: boolean }>`
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;

  @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 77px;
  }

  ${Button};
`;

const ButtonRight = styled(RectangularButtonFilled)<{ isMobile: boolean }>`
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    min-width: 77px;
  }
  
  ${Button};
`;

const ButtonWrapper = styled(FlexBox)`
  flex-direction: row;
  gap: 16px;
  
  @media (max-width: ${LayoutConst.screen.sm}px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const FullWidthButton = styled(RectangularButtonFilled)<{ isMobile: boolean }>`
  width: 100%;
  min-width: inherit;
  height: 46px;
  line-height: 22px;
  font-size: 17px;
`;

interface RequestPublicationModalProps {
  project: Project;
  onConfirm: () => void;
  onCancel: () => void;
}

export const RequestPublicationModal: FC<RequestPublicationModalProps> = ({ project, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isRequestSended = useMemo(() => project?.contexts?.includes('unbanrequest'), [project]);

  const hasApprovedContexts = useMemo(() => {
    return project?.contexts?.some(
      (ctx: string) => !['banned', 'places', 'hsedesigntemp', 'unbanrequest'].includes(ctx)
    );
  }, [project]);
  
  const shouldShowAlreadyUnderReview = isRequestSended || hasApprovedContexts;
  
  const [requestSent, setRequestSent] = useState(false);
  
  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onCancel();
  };

  const handleConfirm = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsSubmitted(true);
  };

  const handleClose = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (hasApprovedContexts) {
      onCancel();
      return;
    }
    
    if (requestSent) {
      onCancel();
      return;
    }
    
    try {
      const response = await API.request()
        .endpoint(Endpoints.geograffee)
        .url(`/api/Project/UpdateContexts`)
        .post()
        .body({
          projectId: project?.projectId,
          contexts: [
            {
              context: "unbanrequest",
              operation: 1,
            }
          ],
        })
        .send();
      
      if (!response.ok) {
        if (response.status === 400) {
          setRequestSent(true);
          onCancel();
          return;
        }
        
        throw new Error(`API error: ${response.status}`);
      }
      
      setRequestSent(true);
      
      window.location.reload();
    } catch (error: any) {
      setRequestSent(true);
      onCancel();
    }
  };

  return (
    <OuterWrapper onClick={(e) => {
      e.stopPropagation();
      e.preventDefault();
    }}>
      <Wrapper direction={'column'} width={'100%'} justify='flex-start' align='flex-start' onClick={(e) => e.stopPropagation()}>
        <FlexBox direction={'column'} directionMobile='column' width={'100%'} justify={'flex-start'}
                 justifyMobile='center'
                 align={'flex-start'} alignMobile={'flex-start'}>
          {shouldShowAlreadyUnderReview ? (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.unbanrequest_title')}
              </Title>
              <FlexBox offsetTop={32} width="100%">
                <FullWidthButton
                  isMobile={isMobile}
                  onClick={handleCancel}
                >
                  {t('request_publication_modal.success_button')}
                </FullWidthButton>
              </FlexBox>
            </>
          ) : isSubmitted ? (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.success_title')}
              </Title>
              <FlexBox offsetTop={32} width="100%">
                <FullWidthButton
                  isMobile={isMobile}
                  onClick={handleClose}
                >
                  {t('request_publication_modal.success_button')}
                </FullWidthButton>
              </FlexBox>
            </>
          ) : (
            <>
              <Title isMobile={isMobile}>
                {t('request_publication_modal.title')}
              </Title>
              <Description isMobile={isMobile}>
                {t('request_publication_modal.description')}
              </Description>
              <ButtonWrapper offsetTop={8} width="100%">
                <ButtonLeft
                  isMobile={isMobile}
                  onClick={handleCancel}
                >
                  {t('request_publication_modal.cancel')}
                </ButtonLeft>
                <ButtonRight
                  isMobile={isMobile}
                  onClick={handleConfirm}
                >
                  {t('request_publication_modal.confirm')}
                </ButtonRight>
              </ButtonWrapper>
            </>
          )}
        </FlexBox>
      </Wrapper>
    </OuterWrapper>
  );
};

